import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import './i18n'; // Initialize i18n
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import Proformas from './pages/Proformas';
import Invoices from './pages/Invoices';
import Payments from './pages/Payments';
import Vessels from './pages/Vessels';
import PortCalls from './pages/PortCalls';
import Documents from './pages/Documents';
import AdminPanel from './pages/AdminPanel';
import Settings from './pages/Settings';
import Anchorage from './pages/Anchorage';
import Raporlama from './pages/Raporlama';
import MotorLaunches from './pages/MotorLaunches';
import TariffCalculator from './pages/TariffCalculator';
import TransactionTracking from './pages/TransactionTracking';
import Layout from './components/Layout';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ExchangeRatesProvider } from './contexts/ExchangeRatesContext';

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" />;
};

// Route with permission check
const ProtectedRoute = ({ children, permissionKey }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/login" />;
  
  // Check permission
  const permissions = user?.module_permissions || {};
  if (permissionKey && permissions[permissionKey] === false) {
    // Yetkili olduğu ilk sayfaya yönlendir
    const firstAllowedRoute = getFirstAllowedRoute(permissions);
    return <Navigate to={firstAllowedRoute} replace />;
  }
  
  return children;
};

// Kullanıcının yetkili olduğu ilk sayfayı bul
const getFirstAllowedRoute = (permissions) => {
  const routePermissions = [
    { path: '/dashboard', key: 'dashboard' },
    { path: '/port-calls', key: 'port_calls' },
    { path: '/proformas', key: 'proformas' },
    { path: '/invoices', key: 'invoices' },
    { path: '/payments', key: 'payments' },
    { path: '/clients', key: 'clients' },
    { path: '/vessels', key: 'vessels' },
    { path: '/motor-launches', key: 'motor_launches' },
    { path: '/anchorage', key: 'anchorage' },
    { path: '/documents', key: 'documents' },
    { path: '/raporlama', key: 'reports' },
    { path: '/settings', key: 'settings' },
  ];
  
  for (const route of routePermissions) {
    if (permissions[route.key] !== false) {
      return route.path;
    }
  }
  
  // Hiçbir yetkisi yoksa login'e yönlendir
  return '/login';
};

// Default route - yetkili olunan ilk sayfaya yönlendir
const DefaultRoute = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/login" />;
  
  const permissions = user?.module_permissions || {};
  const firstAllowedRoute = getFirstAllowedRoute(permissions);
  
  return <Navigate to={firstAllowedRoute} replace />;
};

function App() {
  return (
    <AuthProvider>
      <ExchangeRatesProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<DefaultRoute />} />
            <Route path="/dashboard" element={
              <ProtectedRoute permissionKey="dashboard">
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
          } />
          <Route path="/clients" element={
            <ProtectedRoute permissionKey="clients">
              <Layout>
                <Clients />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/proformas" element={
            <ProtectedRoute permissionKey="proformas">
              <Layout>
                <Proformas />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/invoices" element={
            <ProtectedRoute permissionKey="invoices">
              <Layout>
                <Invoices />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/payments" element={
            <ProtectedRoute permissionKey="payments">
              <Layout>
                <Payments />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/transaction-tracking" element={
            <ProtectedRoute permissionKey="invoices">
              <Layout>
                <TransactionTracking />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/vessels" element={
            <ProtectedRoute permissionKey="vessels">
              <Layout>
                <Vessels />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/port-calls" element={
            <ProtectedRoute permissionKey="port_calls">
              <Layout>
                <PortCalls />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/documents" element={
            <ProtectedRoute permissionKey="documents">
              <Layout>
                <Documents />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin" element={
            <ProtectedRoute permissionKey="admin_panel">
              <Layout>
                <AdminPanel />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute permissionKey="settings">
              <Layout>
                <Settings />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/anchorage" element={
            <ProtectedRoute permissionKey="anchorage">
              <Layout>
                <Anchorage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/raporlama" element={
            <ProtectedRoute permissionKey="reports">
              <Layout>
                <Raporlama />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/motor-launches" element={
            <ProtectedRoute permissionKey="motor_launches">
              <Layout>
                <MotorLaunches />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/tariff-calculator" element={
            <ProtectedRoute permissionKey="proformas">
              <Layout>
                <TariffCalculator />
              </Layout>
            </ProtectedRoute>
          } />
        </Routes>
      </BrowserRouter>
    </ExchangeRatesProvider>
  </AuthProvider>
  );
}

export default App;